import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { withOrgDesignAutoEnable } from "src/util/orgDesignDirectFlags";

export type ChangelogFlagBag = {
  enableChangelog?: boolean | null | undefined;
  enableOrgDesign?: boolean | null | undefined;
};

export type ChangelogAttributeFlagBag = ChangelogFlagBag & {
  /** Accepted so callers can pass the workspace bag. Not part of the gate. */
  enableDemoODOptions?: boolean | null | undefined;
  enableOrgChartAttributeOverlays?: boolean | null | undefined;
  /** Accepted so callers can pass the workspace bag. Not part of the gate. */
  enableOrgChartEditableAttributes?: boolean | null | undefined;
};

export const withChangelogAutoEnable = <T extends ChangelogFlagBag>(
  flags: T
): T => withOrgDesignAutoEnable(flags, "enableChangelog");

export const isChangelogEnabled = (
  flags: ChangelogFlagBag | null | undefined
): boolean => Boolean(withChangelogAutoEnable(flags ?? {}).enableChangelog);

/**
 * Changelog attribute rows follow people-attribute overlays.
 * Org design release, beta, and editable attributes are not part of this
 * gate. Editable off still shows entries that arrive from integration.
 */
export const isChangelogAttributesEnabled = (
  flags: ChangelogAttributeFlagBag | null | undefined
): boolean => Boolean(flags?.enableOrgChartAttributeOverlays);

/**
 * Attribute rows load only on a design workspace, and only when org design
 * and people-attribute overlays are both on.
 * Changelog-only (org design off) never requests them.
 * The default workspace never requests them, including when org design is on.
 */
export const changelogAttributesForWorkspace = (
  isDefaultWorkspace: boolean,
  flags: ChangelogAttributeFlagBag | null | undefined
): boolean => {
  if (!flags?.enableOrgDesign || isDefaultWorkspace) return false;
  return isChangelogAttributesEnabled(flags);
};

export const useChangelogAttributesEnabled = (): boolean => {
  const workspace = useWorkspace();
  const flags = useWorkspaceFeatureFlags();
  return changelogAttributesForWorkspace(!!workspace?.isActive, flags);
};
